import { toast, TypeOptions, ToastOptions, Id } from 'react-toastify';
import { twMerge } from '@dynamika-shared/utils/twMerge';
import { Attention, CheckIcon } from '@dynamika-shared/ui/icons';
import { ToastProgressBar } from '@components/ToastProgressBar/ToastProgressBar';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { useQCTaskExecutionsSlice } from '@restore/QCTaskExecutions/QCTaskExecutions.slice';
import { DownloadExportFile } from '@restore/QCTaskExecutions/QCTaskExecutions.types';

export type ToastType = Exclude<TypeOptions, 'default'> | 'readyDownload';
const mapToastType = (type: ToastType): TypeOptions => {
  switch (type) {
    case 'readyDownload':
      return 'success';
    default:
      return type;
  }
};

type MessageProps = { title: string; text: string; };
type ToastProps = MessageProps & { type: ToastType; };
type CreateDefaultOptions = (type: ToastType) => ToastOptions;

export const progressBarColors = {
  success: 'yellowGreen',
  error: 'vermilion',
  info: 'pastelBlue',
  warning: 'vermilion',
} as const;

export const bgColors = {
  success: 'honeydew',
  error: 'seashell',
  info: 'lotion',
  warning: 'seashell',
} as const;

export const tintsCreator = (
  type: ToastType,
  part: 'bg' | 'border',
  opacity: string,
  source: Record<string, string>
) => `${part}-${source[type]}${opacity}`;

export const createDefaultOptions: CreateDefaultOptions = type => ({
  position: 'bottom-right',
  className: twMerge(
    'm-0 mb-[1.6rem] p-2 w-[44rem] border-[1px] rounded-[1.6rem]',
    tintsCreator(type, 'border', '/40', progressBarColors),
    tintsCreator(type, 'bg', '', bgColors)
  ),
  bodyClassName: 'mb-[1.6rem]',
  customProgressBar: true,
  closeOnClick: true,
  progressClassName: twMerge(
    'w-full border border-transparent rounded-full',
    tintsCreator(type, 'bg', '', progressBarColors)
  ),
});

function getProgressClassName(toastProps: ToastOptions | undefined, type: ToastType) {
  if (typeof toastProps?.progressClassName === 'function') {
    return toastProps.progressClassName({
      type: mapToastType(type),
      defaultClassName: '',
      position: 'bottom-right',
      rtl: false,
    });
  }
  return toastProps?.progressClassName;
}


export const NotificationMessage: React.FC<ToastProps & { toastProps?: ToastOptions }> = ({
  title,
  text,
  type,
  toastProps = {},
}) => {
  const progressClassName = getProgressClassName(toastProps, type);
  const autoClose = Number.isFinite(toastProps.autoClose) ? toastProps.autoClose : 5000;

  return (
    <div className="msg-container mb-[1rem] text-black ml-0.5 mt-[0.7rem] w-[44rem] relative">
      <p className="msg-title text-1424 -mt-0.5 font-semibold">{title}</p>
      <p className="msg-description font-medium mb-[1.6rem]">{text}</p>
      <span
        className={twMerge(
          'mb-[1.6rem]  absolute left-0 bottom-0 h-[0.5rem] w-[42rem] rounded-full',
          tintsCreator(type, 'bg', '/20', progressBarColors)
        )}
      >
        <ToastProgressBar
          {...toastProps}
          progressClassName={progressClassName}
          autoClose={autoClose}
        />
      </span>
    </div>
  );
};

const NotificationMessageDownload: React.FC<ToastProps & { toastProps?: ToastOptions }> = ({
  title,
  text,
  toastProps = {},
}) => {
  const [downloadExportFile] = useQCTaskExecutionsSlice(
    (_, actions) => [actions.downloadExportFile]
  );
  const toastrData = toastProps.data as DownloadExportFile;

  const handleDownload = () => {
    downloadExportFile({
      clinicalTrialId: toastrData.clinicalTrialId,
      taskId: toastrData.taskId,
      taskExecutionInstanceId: toastrData.taskExecutionInstanceId,
      fileName: toastrData.fileName,
    });
  };

  return <div className="msg-container mb-[1rem] text-black ml-0.5 mt-[0.7rem] w-[44rem] relative">
    <p className="msg-title text-1424 -mt-0.5 font-semibold">{title}</p>
    <p className="msg-description font-medium mb-[1.6rem]">{text}</p>

    <div className="flex items-center justify-between gap-2">
      <Button
        size="small"
        type="secondary"
        darkMode
        className="text-white px-4 w-[164px] rounded-md"
        onClick={handleDownload}
      >
        Download
      </Button>
      <Button
        size="small"
        type="default"
        className="bg-white text-black px-4 w-[164px] rounded-md"
      >
        Cancel
      </Button>
    </div>
  </div>;
};

const showToast = (
  method: (content: React.ReactNode, opts?: ToastOptions) => Id,
  props: ToastProps,
  toastOpts?: ToastOptions
) => method(
  <NotificationMessage {...props} toastProps={toastOpts} />,
  { ...toastOpts }
);

const showDownloadToast = (
  method: (content: React.ReactNode, opts?: ToastOptions) => Id,
  props: ToastProps,
  toastOpts?: ToastOptions
) => method(
  <NotificationMessageDownload {...props} toastProps={toastOpts} />,
  { ...toastOpts }
);

export const toaster = Object.assign(
  (props: ToastProps, opts: ToastOptions) => showToast(toast, props, opts),
  {
    success: (props: ToastProps, opts?: ToastOptions) => showToast(toast.success, props, opts),
    error: (props: ToastProps, opts?: ToastOptions) => showToast(toast.error, props, opts),
    info: (props: ToastProps, opts?: ToastOptions) => showToast(toast.info, props, opts),
    warning: (props: ToastProps, opts?: ToastOptions) =>
      showToast(toast.warning, props, {
        icon: <Attention className="text-vermilion" />,
        ...opts,
      }),
      readyDownload: (props: ToastProps, opts?: ToastOptions) => showDownloadToast(toast.success, props,  {
      icon: <CheckIcon className="text-yellowGreen" />,
      ...opts,
    }),
  }
);
